"use client"

import { useCallback, useState, useEffect, useRef, type ReactNode } from "react"
import { type components } from "umbraco/types/schema"
import { resolveLinkHref } from "umbraco"
import { Link, getNonUmbracoUrl } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { useScrollScrub } from "../../transitions/useScrollScrub"
import {
  Root,
  ContentSection,
  TitleGroup,
  Supertitle,
  Headline,
  PercentHighlight,
  TimerText,
  CTAButton,
  CTAText,
} from "./styles"

type ValuePropositionBannerData =
  components["schemas"]["ValuePropositionBannerBlockPropertiesModel"]

interface Props {
  data: ValuePropositionBannerData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

function renderHeadline(text: string): ReactNode {
  const parts = text.split(/(\d+%)/g)
  if (parts.length === 1) return text
  return parts.map((part, i) =>
    /^\d+%$/.test(part) ? (
      <PercentHighlight key={i}>{part}</PercentHighlight>
    ) : (
      part
    ),
  )
}

export default function ValuePropositionBanner({ data }: Props) {
  const { supertitle, headline, countdownEndDate, ctaLabel, ctaLink } = data
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const [hasMounted, setHasMounted] = useState(false)

  // EGDE-318: always on — per the ticket only contentCard / logosStrip /
  // powerNumbers carry a CMS toggle; every other component animates by
  // default. EGDE-479: the original 0.5→0.2 window (band scrolls in empty,
  // copy forms near the viewport top) was rejected by QA — the band read as
  // blank and its invisible CTA was clickable. The copy now forms as the
  // band enters and is fully visible by the time it reaches mid-viewport.
  const rootRef = useRef<HTMLDivElement>(null)
  useScrollScrub(rootRef, {
    startVh: 0.9,
    endVh: 0.55,
    onProgress: useCallback((p: number) => {
      const root = rootRef.current
      if (!root) return
      root.style.setProperty("--scrub", String(p))
      // EGDE-479: block clicks on the CTA while the copy is still faded out.
      root.style.setProperty("--content-clicks", p >= 0.5 ? "auto" : "none")
    }, []),
  })

  useEffect(() => {
    setHasMounted(true)

    if (!countdownEndDate) return

    const endTime = new Date(countdownEndDate).getTime()

    const tick = () => {
      const diff = endTime - Date.now()
      if (diff <= 0) {
        setTimeLeft(0)
        return false
      }
      setTimeLeft(diff)
      return true
    }

    if (!tick()) return

    const interval = setInterval(() => {
      if (!tick()) clearInterval(interval)
    }, 1000)

    return () => clearInterval(interval)
  }, [countdownEndDate])

  const ctaResolved = resolveLinkHref(ctaLink, {
    getNonUmbracoUrl,
    getUmbracoMediaUrl,
  })
  const href = ctaResolved?.href ?? null
  const ctaLinkType = ctaResolved?.isInternal ? "Content" : undefined
  const ctaTarget = ctaResolved?.target

  const showTimer = hasMounted && timeLeft !== null && timeLeft > 0

  return (
    <Root ref={rootRef}>
      <ContentSection>
        <TitleGroup>
          {supertitle && <Supertitle>{supertitle}</Supertitle>}
          {headline && <Headline>{renderHeadline(headline)}</Headline>}
        </TitleGroup>
        {showTimer && <TimerText>{formatCountdown(timeLeft)}</TimerText>}
      </ContentSection>
      {ctaLabel && href && (
        <CTAButton
          {...(ctaLinkType === "Content" ? { as: Link } : {})}
          href={href}
          target={ctaTarget}
          rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
        >
          <CTAText>{ctaLabel}</CTAText>
        </CTAButton>
      )}
    </Root>
  )
}
